import { ArrowRight, Loader2, ShieldCheck } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { FormEvent, JSX, RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { navigateTo } from '../lib/spa';
import { slugifyRoomName } from '../lib/slugify';
import { createRoom, fetchRoomAvailability, RoomApiError } from '../api/rooms';

interface JoinRoomCardProps {
  /** Ref of the slug input; the host focuses it from the «Join» quick action and nav entries. */
  readonly inputRef: RefObject<HTMLInputElement | null>;
  /**
   * The SSO session's access token, or null for guests. Joining never needs it; creating a
   * room (quick session and custom address) is authenticated - a guest is taken into the
   * sign-in flow instead of drawing a 401.
   */
  readonly token: string | null;
  /** Starts the PKCE sign-in; the create affordances call it instead of failing. */
  readonly onRequireSignIn: () => void;
  /** Fetch override for tests; defaults to the global fetch. */
  readonly fetchImpl?: typeof fetch | undefined;
}

/** State of the live slug availability check (CR-14) behind the custom-address form. */
type AvailabilityState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'invalid' }
  | { readonly kind: 'checking' }
  | { readonly kind: 'available' }
  | { readonly kind: 'taken' }
  | { readonly kind: 'error' };

/** The debounce window of the availability query, so typing does not hammer the endpoint. */
const AVAILABILITY_DEBOUNCE_MS = 350;

/** The TTL picker options (CR-26): minutes plus their i18n keys; 0 = the room never expires. */
const TTL_OPTIONS: ReadonlyArray<{ readonly minutes: number; readonly labelKey: string }> = [
  { minutes: 60, labelKey: 'home.create_ttl_1h' },
  { minutes: 1440, labelKey: 'home.create_ttl_24h' },
  { minutes: 10080, labelKey: 'home.create_ttl_7d' },
  { minutes: 43200, labelKey: 'home.create_ttl_30d' },
  { minutes: 0, labelKey: 'home.create_ttl_never' },
];

/** The picker's default: the server's own default (24h), spelled out for the user. */
const DEFAULT_TTL_MINUTES = 1440;

/**
 * The join form of the former landing home, folded into the dashboard: the room field takes the
 * user's arbitrary name (CR-26) - «Моя Встреча» joins /r/moya-vstrecha - slugified client-side
 * with the same table the server registers through, then a plain SPA navigation to /r/<slug>.
 * Rendered for guests and signed-in users alike — joining a room never needs an SSO session.
 *
 * The create mode (CR-14) lives in the same card: signed-in users expand «создать со своим
 * адресом», type a name whose slugified availability is checked live against the public
 * availability endpoint (debounced; green «свободно» / red «занят»; the response's slug echo
 * fills the field), pick the room's lifetime (the CR-26 TTL picker), optionally arm the room's
 * lobby, and land inside the created room (the creator becomes its owner and bypasses the lobby).
 */
export function JoinRoomCard({
  inputRef,
  token,
  onRequireSignIn,
  fetchImpl,
}: JoinRoomCardProps): JSX.Element {
  const { t } = useTranslation();
  const [slug, setSlug] = useState('');
  const [error, setError] = useState<string | null>(null);

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    // CR-26: the field holds the arbitrary name; navigation goes to the registered slug the
    // same transliteration the server applies produces. A name with fewer than three
    // surviving characters is refused before any navigation.
    const registered = slugifyRoomName(slug.trim());
    if (registered === null) {
      setError(t('home.slug_error'));
      return;
    }
    setError(null);
    setSlug(registered);
    navigateTo(`/r/${registered}`);
  }

  return (
    <section
      aria-label={t('home.card_title')}
      data-testid="dashboard-join-card"
      className="mt-8 rounded-xl border border-border bg-card p-5"
    >
      <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
        {t('home.card_title')}
      </p>
      <form
        onSubmit={handleSubmit}
        className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-start"
        aria-label={t('home.join_form_aria')}
      >
        <div className="flex h-10 min-w-0 flex-1 items-center rounded-md border border-input bg-input/30 shadow-xs transition-[color,box-shadow] focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50">
          <span
            aria-hidden
            className="shrink-0 whitespace-nowrap pl-3 font-mono text-sm text-zinc-500"
          >
            /r/
          </span>
          <input
            id="dashboard-join-input"
            ref={inputRef}
            value={slug}
            onChange={(event) => {
              setSlug(event.target.value);
              setError(null);
            }}
            placeholder={t('home.room_name_placeholder')}
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            aria-invalid={error !== null}
            aria-label={t('home.room_name_label')}
            data-testid="dashboard-join-input"
            className="h-full w-full min-w-0 bg-transparent px-2 text-sm outline-none placeholder:text-muted-foreground"
          />
        </div>
        <Button type="submit" className="h-10 shrink-0 gap-1.5 px-5 font-semibold">
          {t('home.join_room')}
          <ArrowRight aria-hidden className="size-4" />
        </Button>
      </form>
      {error !== null ? (
        <p role="alert" className="mt-2 text-sm text-red-300">
          {error}
        </p>
      ) : null}
      <CreateRoomSection token={token} onRequireSignIn={onRequireSignIn} fetchImpl={fetchImpl} />
    </section>
  );
}

/**
 * The custom-address create form (CR-14): collapsed behind a toggle, expanded it validates the
 * name's slugified shape locally, checks availability live (debounced, on the slugified value;
 * the response echo fills the field), picks the room's lifetime (the CR-26 TTL picker), arms the
 * lobby option on demand and creates through the authenticated endpoint. Guests expanding it are
 * taken into the sign-in flow - the submit button never draws a 401.
 */
function CreateRoomSection({
  token,
  onRequireSignIn,
  fetchImpl,
}: {
  readonly token: string | null;
  readonly onRequireSignIn: () => void;
  readonly fetchImpl?: typeof fetch | undefined;
}): JSX.Element {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [slug, setSlug] = useState('');
  const [lobby, setLobby] = useState(false);
  const [ttlMinutes, setTtlMinutes] = useState(DEFAULT_TTL_MINUTES);
  const [availability, setAvailability] = useState<AvailabilityState>({ kind: 'idle' });
  const [submitting, setSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  /** Serializes availability answers: only the latest issued query may write state. */
  const querySeq = useRef(0);

  const doFetch = fetchImpl ?? globalThis.fetch;

  // The live availability check (CR-14, CR-26): debounced, shape-gated on the SLUGIFIED value,
  // race-guarded. A stale answer (an older query resolving after a newer keystroke) is dropped
  // by the sequence counter; the answer's slug echo fills the field, so the user always sees
  // the registered address their name resolves to.
  useEffect(() => {
    const candidate = slug.trim();
    if (candidate.length === 0) {
      setAvailability({ kind: 'idle' });
      return undefined;
    }
    const registered = slugifyRoomName(candidate);
    if (registered === null) {
      setAvailability({ kind: 'invalid' });
      return undefined;
    }
    setAvailability({ kind: 'checking' });
    const seq = querySeq.current + 1;
    querySeq.current = seq;
    const timer = setTimeout((): void => {
      fetchRoomAvailability(registered, doFetch)
        .then((answer): void => {
          if (querySeq.current !== seq) {
            return;
          }
          // The echo fills the field: the user typed «Моя Встреча», the field now reads the
          // registered moya-vstrecha (the next effect pass re-checks the same slug - a no-op).
          setSlug(answer.slug);
          setAvailability(answer.available ? { kind: 'available' } : { kind: 'taken' });
        })
        .catch((): void => {
          if (querySeq.current !== seq) {
            return;
          }
          setAvailability({ kind: 'error' });
        });
    }, AVAILABILITY_DEBOUNCE_MS);
    return (): void => {
      clearTimeout(timer);
    };
  }, [slug, doFetch]);

  const canSubmit = token !== null && !submitting && availability.kind === 'available';

  const handleCreate = useCallback((): void => {
    if (token === null) {
      // Guests are taken into the sign-in flow instead of drawing a 401 from the endpoint.
      onRequireSignIn();
      return;
    }
    const candidate = slug.trim();
    if (slugifyRoomName(candidate) === null || availability.kind !== 'available') {
      return;
    }
    setSubmitting(true);
    setCreateError(null);
    createRoom(
      token,
      {
        slug: candidate,
        kind: 'meeting',
        ttlMinutes,
        ...(lobby ? { lobby: true } : {}),
      },
      doFetch,
    )
      .then((created): void => {
        navigateTo(`/r/${created.name}`);
      })
      .catch((err: unknown): void => {
        setSubmitting(false);
        // The server is the single conflict decision point: a 409 between the availability
        // check and the create flips the line to «занят»; everything else is a message.
        if (err instanceof RoomApiError && err.status === 409) {
          setAvailability({ kind: 'taken' });
        }
        setCreateError(
          err instanceof RoomApiError && err.status === 409
            ? t('home.create_taken')
            : t('home.create_failed'),
        );
      });
  }, [token, slug, availability.kind, ttlMinutes, lobby, onRequireSignIn, doFetch, t]);

  function handleToggleOpen(next: boolean): void {
    setCreateError(null);
    if (next && token === null) {
      // Expanding the create form as a guest goes straight to the sign-in flow; the form
      // stays collapsed (it is useless without the session).
      onRequireSignIn();
      return;
    }
    setOpen(next);
  }

  const availabilityLine = (): JSX.Element | null => {
    switch (availability.kind) {
      case 'checking':
        return (
          <span className="flex items-center gap-1 text-muted-foreground">
            <Loader2 aria-hidden className="size-3 animate-spin" />
            {t('home.create_checking')}
          </span>
        );
      case 'available':
        return <span className="text-emerald-400">{t('home.create_available')}</span>;
      case 'taken':
        return <span className="text-red-400">{t('home.create_taken')}</span>;
      case 'invalid':
        return <span className="text-amber-400">{t('home.create_invalid')}</span>;
      case 'error':
        return <span className="text-amber-400">{t('home.create_check_error')}</span>;
      default:
        return null;
    }
  };

  return (
    <div className="mt-4 border-t border-border pt-4">
      <button
        type="button"
        data-testid="create-slug-toggle"
        aria-expanded={open}
        onClick={() => {
          handleToggleOpen(!open);
        }}
        className="flex items-center gap-1.5 text-[12.5px] font-medium text-muted-foreground transition-colors hover:text-foreground"
      >
        <ShieldCheck aria-hidden className="size-3.5" />
        {t('home.create_toggle')}
      </button>
      {open ? (
        <form
          className="mt-3 flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            handleCreate();
          }}
          aria-label={t('home.create_form_aria')}
        >
          <div>
            <Label
              htmlFor="create-slug-input"
              className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground"
            >
              {t('home.create_slug_label')}
            </Label>
            <div className="mt-2 flex h-10 items-center rounded-md border border-input bg-input/30 shadow-xs transition-[color,box-shadow] focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50">
              <span
                aria-hidden
                className="shrink-0 whitespace-nowrap pl-3 font-mono text-sm text-zinc-500"
              >
                /r/
              </span>
              <Input
                id="create-slug-input"
                data-testid="create-slug-input"
                value={slug}
                onChange={(event) => {
                  setSlug(event.target.value);
                  setCreateError(null);
                }}
                placeholder={t('home.room_name_placeholder')}
                autoComplete="off"
                autoCapitalize="none"
                spellCheck={false}
                className="h-full w-full min-w-0 rounded-none border-0 bg-transparent px-2 text-sm shadow-none focus-visible:ring-0 focus-visible:border-0 dark:bg-transparent"
              />
            </div>
            <p
              aria-live="polite"
              data-testid="slug-availability"
              className="mt-1 min-h-4 text-[11.5px]"
            >
              {availabilityLine()}
            </p>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label
                htmlFor="create-ttl-select"
                className="text-[12px] font-medium text-foreground"
              >
                {t('home.create_ttl_label')}
              </Label>
              <p className="text-[11px] text-muted-foreground">{t('home.create_ttl_hint')}</p>
            </div>
            {/* Native select: the room lifetime is a settings-like choice, not a primary
                action - the closed shadcn Select adds a popover for one flat list. */}
            <select
              id="create-ttl-select"
              data-testid="create-ttl-select"
              value={ttlMinutes}
              onChange={(event) => {
                setTtlMinutes(Number(event.target.value));
              }}
              aria-label={t('home.create_ttl_label')}
              className="h-9 self-center rounded-md border border-input bg-input/30 px-2 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
            >
              {TTL_OPTIONS.map((option) => (
                <option key={option.minutes} value={option.minutes}>
                  {t(option.labelKey)}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <Label
                htmlFor="create-lobby-toggle"
                className="text-[12px] font-medium text-foreground"
              >
                {t('home.create_lobby')}
              </Label>
              <p className="text-[11px] text-muted-foreground">{t('home.create_lobby_hint')}</p>
            </div>
            <Switch
              id="create-lobby-toggle"
              data-testid="create-lobby-toggle"
              checked={lobby}
              onCheckedChange={setLobby}
              aria-label={t('home.create_lobby')}
            />
          </div>
          {createError !== null ? (
            <p role="alert" className="text-[12px] text-red-300">
              {createError}
            </p>
          ) : null}
          <Button
            type="submit"
            className="h-10 shrink-0 gap-1.5 px-5 font-semibold"
            disabled={!canSubmit}
            data-testid="create-slug-submit"
          >
            {submitting ? <Loader2 aria-hidden className="size-4 animate-spin" /> : null}
            {t('home.create_submit')}
          </Button>
        </form>
      ) : null}
    </div>
  );
}
